import type { RaRecord } from "react-admin";

import {
  formatNarrationIts,
  formatNarrationRupee,
  formatNarrationTransactionId,
} from "./receipt-narration-format";

export type YearlyNiyaazReceiptNarrationInput = {
  itsNo?: unknown;
  name?: unknown;
  amount?: unknown;
  paymentMode?: unknown;
  paymentRef?: unknown;
  transactionId?: unknown;
  formNo?: unknown;
  hijriYear?: unknown;
};

export type YearlyNiyaazReceiptNarrationOptions = {
  includeItsNo?: boolean;
  includeName?: boolean;
  includeAmount?: boolean;
  includePaymentMode?: boolean;
  includeTransactionId?: boolean;
  includeFormNo?: boolean;
  includeYear?: boolean;
};

function parseAmount(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? Math.round(n) : 0;
}

/** ITS, name, paid amount, payment mode, txn id, form/year — Tally export (not printed on receipt). */
export function formatYearlyNiyaazReceiptNarration(
  input: YearlyNiyaazReceiptNarrationInput,
  options?: YearlyNiyaazReceiptNarrationOptions
): string {
  const includeItsNo = options?.includeItsNo ?? true;
  const includeName = options?.includeName ?? true;
  const includeAmount = options?.includeAmount ?? true;
  const includePaymentMode = options?.includePaymentMode ?? true;
  const includeTransactionId = options?.includeTransactionId ?? true;
  const includeFormNo = options?.includeFormNo ?? true;
  const includeYear = options?.includeYear ?? true;

  const itsPart = includeItsNo ? formatNarrationIts(input.itsNo) : "";
  const name = includeName ? String(input.name ?? "").trim() : "";
  const paidAmount = includeAmount ? formatNarrationRupee(parseAmount(input.amount)) : "";
  const paymentMode = includePaymentMode
    ? String(input.paymentMode ?? "")
        .trim()
        .toUpperCase()
    : "";
  const txnPart = includeTransactionId
    ? formatNarrationTransactionId(input.transactionId ?? input.paymentRef)
    : "";
  const formNo = includeFormNo ? String(input.formNo ?? "").trim() : "";
  const hijriYear = String(input.hijriYear ?? "").trim();
  const yearPart = includeYear && hijriYear ? `Hijri ${hijriYear}` : "";

  return [itsPart, name, paidAmount, paymentMode, txnPart, formNo, yearPart]
    .filter((part) => part !== "")
    .join(", ");
}

export function formatYearlyNiyaazReceiptNarrationFromRecord(
  rec: RaRecord,
  options?: YearlyNiyaazReceiptNarrationOptions
): string {
  const yn = rec.yearlyNiyaaz as
    | { itsNo?: string; name?: string; formNo?: string; hijriYear?: string | number }
    | undefined;
  return formatYearlyNiyaazReceiptNarration(
    {
      itsNo: yn?.itsNo,
      name: yn?.name,
      amount: rec.amount,
      paymentMode: rec.paymentMode,
      paymentRef: rec.paymentRef,
      transactionId: rec.transactionId,
      formNo: yn?.formNo,
      hijriYear: yn?.hijriYear,
    },
    options
  );
}
